import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminFailedFlow,
  AdminFailedFlowPage,
  AdminIncidentLogArtifactData,
  AdminFailedFlowQuery,
  AdminMediaArtifactData,
  AdminQueryEnvelope,
  AdminUserDataDetail,
  AdminUserLookupInput,
} from "../../contracts/admin-read-models";
import {
  appendUserMediaWorkspaceFocus,
  type UserMediaWorkspaceScenarioKey,
} from "../users/media-workspace-focus";
import { appendUserSameDeviceWorkspaceContext } from "../users/same-device-workspace-context";

export type AdminFailedFlowDrilldownTarget = {
  request?: string;
  correlation?: string;
  jobs?: string;
  userSummary?: string;
  userMedia?: string;
  deviceErrors?: string;
  deviceKpis?: string;
  releaseRegression?: string;
};

export function isFailedFlowMediaRelevant(flow: AdminFailedFlow): boolean {
  return (
    flow.fileId !== undefined ||
    (flow.signalKey === "pet_profile" && flow.stage === "pet_photo_confirm") ||
    flow.signalKey === "daily_media" ||
    (flow.signalKey === "health_record" &&
      (flow.stage === "health_file_upload" ||
        flow.stage === "health_file_confirm" ||
        flow.stage === "health_analysis_process" ||
        flow.stage === "health_record_save")) ||
    (flow.signalKey === "receipt_record" &&
      (flow.stage === "receipt_extract" || flow.stage === "receipt_save")) ||
    (flow.signalKey === "community_post" &&
      (flow.stage === "community_image_upload" || flow.fileId !== undefined)) ||
    (flow.signalKey === "file_lifecycle" && flow.stage === "file_cleanup")
  );
}

export function isFailedFlowSameDeviceLogRelevant(flow: AdminFailedFlow): boolean {
  return (
    (flow.signalKey === "pet_profile" && flow.stage === "pet_photo_confirm") ||
    flow.signalKey === "daily_media" ||
    (flow.signalKey === "receipt_record" &&
      (flow.stage === "receipt_extract" || flow.stage === "receipt_save")) ||
    (flow.signalKey === "community_post" && flow.stage === "community_image_upload")
  );
}

export function inferFailedFlowMediaScenarioKey(
  flow: AdminFailedFlow,
): UserMediaWorkspaceScenarioKey | undefined {
  if (flow.signalKey === "pet_profile" && flow.stage === "pet_photo_confirm") {
    return "pet_profile";
  }

  if (flow.signalKey === "daily_media") {
    return "daily_record";
  }

  if (flow.signalKey === "receipt_record") {
    return "receipt";
  }

  if (flow.signalKey === "health_record") {
    return "health_record";
  }

  if (
    flow.signalKey === "community_post" &&
    (flow.stage === "community_image_upload" || flow.fileId !== undefined)
  ) {
    return "community_post";
  }

  if (flow.signalKey === "file_lifecycle" && flow.stage === "file_cleanup") {
    return "unknown";
  }

  return undefined;
}

export function inferFailedFlowMediaLinkedEntity(
  flow: AdminFailedFlow,
):
  | {
      linkedEntityType: NonNullable<AdminMediaArtifactData["linkedEntityType"]>;
      linkedEntityId: string;
    }
  | undefined {
  if (flow.signalKey === "pet_profile") {
    if (flow.petIdHash) {
      return { linkedEntityType: "pet", linkedEntityId: flow.petIdHash };
    }

    if (flow.userIdHash) {
      return { linkedEntityType: "user", linkedEntityId: flow.userIdHash };
    }

    return undefined;
  }

  if (flow.signalKey === "daily_media") {
    if (flow.recordId) {
      return { linkedEntityType: "record", linkedEntityId: flow.recordId };
    }

    if (flow.petIdHash) {
      return { linkedEntityType: "pet", linkedEntityId: flow.petIdHash };
    }

    if (flow.userIdHash) {
      return { linkedEntityType: "user", linkedEntityId: flow.userIdHash };
    }

    return undefined;
  }

  if (flow.signalKey === "community_post") {
    if (flow.petIdHash) {
      return { linkedEntityType: "pet", linkedEntityId: flow.petIdHash };
    }

    if (flow.userIdHash) {
      return { linkedEntityType: "user", linkedEntityId: flow.userIdHash };
    }

    if (flow.fileId) {
      return { linkedEntityType: "file", linkedEntityId: flow.fileId };
    }

    return undefined;
  }

  if (flow.signalKey === "health_record") {
    if (flow.recordId) {
      return { linkedEntityType: "record", linkedEntityId: flow.recordId };
    }

    if (flow.petIdHash) {
      return { linkedEntityType: "pet", linkedEntityId: flow.petIdHash };
    }

    if (flow.userIdHash) {
      return { linkedEntityType: "user", linkedEntityId: flow.userIdHash };
    }

    return undefined;
  }

  if (flow.signalKey === "receipt_record") {
    if (flow.receiptId) {
      return { linkedEntityType: "receipt", linkedEntityId: flow.receiptId };
    }

    if (flow.recordId) {
      return { linkedEntityType: "record", linkedEntityId: flow.recordId };
    }

    if (flow.petIdHash) {
      return { linkedEntityType: "pet", linkedEntityId: flow.petIdHash };
    }

    if (flow.userIdHash) {
      return { linkedEntityType: "user", linkedEntityId: flow.userIdHash };
    }

    return undefined;
  }

  if (flow.signalKey === "file_lifecycle") {
    if (flow.fileId) {
      return { linkedEntityType: "file", linkedEntityId: flow.fileId };
    }

    if (flow.userIdHash) {
      return { linkedEntityType: "user", linkedEntityId: flow.userIdHash };
    }
  }

  if (flow.userIdHash) {
    return { linkedEntityType: "user", linkedEntityId: flow.userIdHash };
  }

  return undefined;
}

function createFailedFlowMediaLookupInput(
  flow: AdminFailedFlow,
): AdminUserLookupInput | undefined {
  if (flow.userIdHash) {
    return { userIdHash: flow.userIdHash };
  }

  if (flow.requestId) {
    return { requestId: flow.requestId };
  }

  if (flow.correlationId) {
    return { correlationId: flow.correlationId };
  }

  return undefined;
}

function createFailedFlowMediaLookupKey(input: AdminUserLookupInput): string {
  return JSON.stringify(input);
}

async function loadFailedFlowUserDataDetailMap(
  port: AdminQueryPort,
  flows: readonly AdminFailedFlow[],
): Promise<Map<string, AdminUserDataDetail>> {
  const cache = new Map<string, Promise<AdminQueryEnvelope<AdminUserDataDetail>>>();
  const lookups = new Map<string, AdminUserLookupInput>();

  for (const flow of flows) {
    const lookup = createFailedFlowMediaLookupInput(flow);
    if (!lookup) {
      continue;
    }

    lookups.set(createFailedFlowMediaLookupKey(lookup), lookup);
  }

  for (const [key, lookup] of lookups.entries()) {
    cache.set(key, port.getUserDataDetail(lookup));
  }

  const settled = await Promise.allSettled(
    [...cache.entries()].map(async ([key, promise]) => [key, await promise] as const),
  );
  const detailsByLookupKey = new Map<string, AdminUserDataDetail>();

  for (const result of settled) {
    if (result.status !== "fulfilled") {
      continue;
    }

    const [key, envelope] = result.value;
    detailsByLookupKey.set(key, envelope.data);
  }

  return detailsByLookupKey;
}

function doesArtifactMatchScenario(
  artifact: AdminMediaArtifactData,
  scenarioKey: UserMediaWorkspaceScenarioKey | undefined,
): boolean {
  if (!scenarioKey || scenarioKey === "unknown") {
    return true;
  }

  return artifact.sourceScenario === scenarioKey;
}

function scoreFailedFlowArtifactMatch(
  flow: AdminFailedFlow,
  artifact: AdminMediaArtifactData,
): number {
  const expectedScenario = inferFailedFlowMediaScenarioKey(flow);
  if (!doesArtifactMatchScenario(artifact, expectedScenario)) {
    return -1;
  }

  if (flow.fileId && artifact.artifactId === flow.fileId) {
    return 400;
  }

  if (
    flow.fileId &&
    artifact.linkedEntityType === "file" &&
    artifact.linkedEntityId === flow.fileId
  ) {
    return 320;
  }

  if (flow.requestId && artifact.latestTraceKey?.requestId === flow.requestId) {
    return 280;
  }

  if (
    flow.correlationId &&
    artifact.latestTraceKey?.correlationId === flow.correlationId
  ) {
    return 240;
  }

  const linkedEntity = inferFailedFlowMediaLinkedEntity(flow);
  if (
    linkedEntity &&
    artifact.linkedEntityType === linkedEntity.linkedEntityType &&
    artifact.linkedEntityId === linkedEntity.linkedEntityId
  ) {
    return 200;
  }

  return -1;
}

export function findMatchedFailedFlowMediaArtifact(
  flow: AdminFailedFlow,
  detail: Pick<AdminUserDataDetail, "mediaArtifacts">,
): AdminMediaArtifactData | undefined {
  if (!isFailedFlowMediaRelevant(flow)) {
    return undefined;
  }

  const artifacts = detail.mediaArtifacts ?? [];
  let bestMatch: { artifact: AdminMediaArtifactData; score: number } | undefined;

  for (const artifact of artifacts) {
    const score = scoreFailedFlowArtifactMatch(flow, artifact);
    if (score < 0) {
      continue;
    }

    if (
      !bestMatch ||
      score > bestMatch.score ||
      (score === bestMatch.score &&
        artifact.uploadedAt.localeCompare(bestMatch.artifact.uploadedAt) > 0)
    ) {
      bestMatch = { artifact, score };
    }
  }

  return bestMatch?.artifact;
}

export async function loadFailedFlowMediaArtifactMap(
  port: AdminQueryPort,
  flows: readonly AdminFailedFlow[],
): Promise<Map<string, AdminMediaArtifactData>> {
  const detailsByLookupKey = await loadFailedFlowUserDataDetailMap(port, flows);

  const matchedArtifacts = new Map<string, AdminMediaArtifactData>();

  for (const flow of flows) {
    const lookup = createFailedFlowMediaLookupInput(flow);
    if (!lookup) {
      continue;
    }

    const detail = detailsByLookupKey.get(createFailedFlowMediaLookupKey(lookup));
    if (!detail) {
      continue;
    }

    const artifact = findMatchedFailedFlowMediaArtifact(flow, detail);
    if (artifact) {
      matchedArtifacts.set(flow.id, artifact);
    }
  }

  return matchedArtifacts;
}

function scoreFailedFlowIncidentLogArtifactMatch(
  flow: AdminFailedFlow,
  artifact: AdminIncidentLogArtifactData,
): number {
  if (flow.requestId && artifact.latestTraceKey?.requestId === flow.requestId) {
    return 400;
  }

  if (
    flow.correlationId &&
    artifact.latestTraceKey?.correlationId === flow.correlationId
  ) {
    return 320;
  }

  if (flow.deviceInstallId && artifact.deviceInstallId === flow.deviceInstallId) {
    return 240;
  }

  if (
    flow.errorCode &&
    artifact.latestTraceKey?.errorCode === flow.errorCode
  ) {
    return 180;
  }

  if (
    artifact.latestTraceKey?.signalKey === flow.signalKey &&
    artifact.latestTraceKey?.stage === flow.stage
  ) {
    return 140;
  }

  return -1;
}

export function findMatchedFailedFlowIncidentLogArtifact(
  flow: AdminFailedFlow,
  detail: Pick<AdminUserDataDetail, "incidentLogArtifacts">,
): AdminIncidentLogArtifactData | undefined {
  if (!isFailedFlowSameDeviceLogRelevant(flow)) {
    return undefined;
  }

  const artifacts = detail.incidentLogArtifacts ?? [];
  let bestMatch: { artifact: AdminIncidentLogArtifactData; score: number } | undefined;

  for (const artifact of artifacts) {
    const score = scoreFailedFlowIncidentLogArtifactMatch(flow, artifact);
    if (score < 0) {
      continue;
    }

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { artifact, score };
    }
  }

  return bestMatch?.artifact;
}

export async function loadFailedFlowIncidentLogArtifactMap(
  port: AdminQueryPort,
  flows: readonly AdminFailedFlow[],
): Promise<Map<string, AdminIncidentLogArtifactData>> {
  const detailsByLookupKey = await loadFailedFlowUserDataDetailMap(port, flows);
  const matchedArtifacts = new Map<string, AdminIncidentLogArtifactData>();

  for (const flow of flows) {
    const lookup = createFailedFlowMediaLookupInput(flow);
    if (!lookup) {
      continue;
    }

    const detail = detailsByLookupKey.get(createFailedFlowMediaLookupKey(lookup));
    if (!detail) {
      continue;
    }

    const artifact = findMatchedFailedFlowIncidentLogArtifact(flow, detail);
    if (artifact) {
      matchedArtifacts.set(flow.id, artifact);
    }
  }

  return matchedArtifacts;
}

function createQueryHref(pathname: string, key: string, value: string | undefined): string | undefined {
  if (value === undefined || value.length === 0) {
    return undefined;
  }

  const params = new URLSearchParams();
  params.set(key, value);
  return `${pathname}?${params.toString()}`;
}

function createJobsHref(flow: AdminFailedFlow): string | undefined {
  const params = new URLSearchParams();

  if (flow.signalKey === "health_record" && flow.stage === "health_analysis_process") {
    params.set("jobType", "health_file_analysis");

    if (flow.fileId) {
      params.set("linkedFileId", flow.fileId);
    }

    if (flow.recordId) {
      params.set("linkedRecordId", flow.recordId);
    }

    if (flow.userIdHash) {
      params.set("userIdHash", flow.userIdHash);
    }

    if (flow.correlationId) {
      params.set("correlationId", flow.correlationId);
    } else if (flow.requestId) {
      params.set("requestId", flow.requestId);
    }

    return `/admin/jobs?${params.toString()}`;
  }

  if (flow.signalKey !== "account_deletion" && !flow.jobId && !flow.deletionJobId) {
    return undefined;
  }

  if (flow.signalKey === "account_deletion") {
    params.set("jobType", "account_deletion");

    if (flow.userIdHash) {
      params.set("userIdHash", flow.userIdHash);
    }

    if (flow.correlationId) {
      params.set("correlationId", flow.correlationId);
      return `/admin/jobs?${params.toString()}`;
    }

    if (flow.requestId) {
      params.set("requestId", flow.requestId);
      return `/admin/jobs?${params.toString()}`;
    }

    if (flow.userIdHash) {
      return `/admin/jobs?${params.toString()}`;
    }
  }

  if (flow.correlationId) {
    params.set("correlationId", flow.correlationId);
    return `/admin/jobs?${params.toString()}`;
  }

  if (flow.requestId) {
    params.set("requestId", flow.requestId);
    return `/admin/jobs?${params.toString()}`;
  }

  return undefined;
}

function createFailedFlowScopeHref(flow: AdminFailedFlow): string | undefined {
  if (flow.deviceInstallId === undefined || flow.deviceInstallId.length === 0) {
    return undefined;
  }

  return createQueryHref("/admin/failed-flows", "deviceInstallId", flow.deviceInstallId);
}

function createDeviceKpiHref(flow: AdminFailedFlow): string | undefined {
  if (flow.deviceInstallId === undefined || flow.deviceInstallId.length === 0) {
    return undefined;
  }

  return createQueryHref("/admin/device-kpis", "deviceInstallId", flow.deviceInstallId);
}

function createReleaseRegressionHref(flow: AdminFailedFlow): string | undefined {
  if (
    (flow.release === undefined || flow.release.length === 0) &&
    (flow.appVersion === undefined || flow.appVersion.length === 0)
  ) {
    return undefined;
  }

  const params = new URLSearchParams();

  if (flow.release) {
    params.set("release", flow.release);
  }

  if (flow.appVersion) {
    params.set("appVersion", flow.appVersion);
  }

  return `/admin/releases?${params.toString()}`;
}

function appendFailedFlowUserWorkspaceContext(
  params: URLSearchParams,
  flow: AdminFailedFlow,
): URLSearchParams {
  return appendUserSameDeviceWorkspaceContext(params, {
    deviceInstallId: flow.deviceInstallId,
    release: flow.release,
    appVersion: flow.appVersion,
    platform: flow.platform,
  });
}

function appendFailedFlowUserLookupScope(
  params: URLSearchParams,
  flow: AdminFailedFlow,
): URLSearchParams {
  const nextParams = new URLSearchParams(params);

  if (flow.requestId) {
    nextParams.set("requestId", flow.requestId);
  }

  if (flow.correlationId) {
    nextParams.set("correlationId", flow.correlationId);
  }

  return nextParams;
}

function createUserSummaryHref(flow: AdminFailedFlow): string | undefined {
  const params = appendFailedFlowUserLookupScope(
    appendFailedFlowUserWorkspaceContext(new URLSearchParams(), flow),
    flow,
  );

  if (flow.requestId || flow.correlationId) {
    return `/admin/users?${params.toString()}`;
  }

  if (flow.userIdHash) {
    params.set("userIdHash", flow.userIdHash);
    return `/admin/users?${params.toString()}`;
  }

  return undefined;
}

function createUserMediaHref(flow: AdminFailedFlow): string | undefined {
  if (!isFailedFlowMediaRelevant(flow)) {
    return undefined;
  }

  const params = appendFailedFlowUserLookupScope(
    appendFailedFlowUserWorkspaceContext(
      appendUserMediaWorkspaceFocus(new URLSearchParams(), {
        fileId: flow.fileId,
        scenarioKey: inferFailedFlowMediaScenarioKey(flow),
      }),
      flow,
    ),
    flow,
  );

  if (flow.requestId || flow.correlationId) {
    return `/admin/users?${params.toString()}`;
  }

  if (flow.userIdHash) {
    params.set("userIdHash", flow.userIdHash);
    return `/admin/users?${params.toString()}`;
  }

  return undefined;
}

export async function searchFailedFlows(
  port: AdminQueryPort,
  query: AdminFailedFlowQuery,
): Promise<AdminQueryEnvelope<AdminFailedFlowPage>> {
  return port.searchFailedFlows(query);
}

export function buildFailedFlowDrilldownTarget(
  flow: AdminFailedFlow,
): AdminFailedFlowDrilldownTarget {
  return {
    request: createQueryHref("/admin/failed-flows", "requestId", flow.requestId),
    correlation: createQueryHref("/admin/failed-flows", "correlationId", flow.correlationId),
    jobs: createJobsHref(flow),
    userSummary: createUserSummaryHref(flow),
    userMedia: createUserMediaHref(flow),
    deviceErrors: createFailedFlowScopeHref(flow),
    deviceKpis: createDeviceKpiHref(flow),
    releaseRegression: createReleaseRegressionHref(flow),
  };
}
